import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/layout/Header";
import footerFunding from "@/assets/footer-funding.jpeg";
import { SEOPresets } from "@/components/SEO";

const SubscriptionBillingTerms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-background">
      {SEOPresets.subscriptionBilling}
      <Header showLogout solidBackground={true} />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-2">
            SUBSCRIPTION AND BILLING TERMS
          </h1>
          <h2 className="text-2xl font-semibold mb-1">
            Sundae Education Technologies SL
          </h2>
          <h3 className="text-xl mb-8">GlobalCoders App</h3>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              1. SUBSCRIPTION OVERVIEW
            </h2>

            <h3 className="text-xl font-semibold mb-3">1.1 Service Model</h3>
            <p className="mb-4">
              GlobalCoders operates on a freemium subscription model where users
              receive limited free access and can upgrade to paid subscriptions
              for full service access.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              1.2 Free Tier Limitations
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Free Simulations: Indicated on the website or mobile application
              </li>
              <li>
                One-Time Allocation: Free simulations are provided once per
                account lifetime
              </li>
              <li>No Renewal: Free tier does not renew or reset</li>
              <li>
                Feature Access: Limited access to basic feedback and performance
                metrics
              </li>
              <li>
                Upgrade Required: Additional simulations require active paid
                subscription
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              1.3 Subscription Benefits
            </h3>
            <p className="mb-2">Paid subscriptions include:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Unlimited Simulations: No restrictions on practice sessions
              </li>
              <li>
                Advanced AI Feedback: Detailed performance analysis and
                improvement recommendations
              </li>
              <li>
                Premium Content: Access to specialized interview scenarios and
                industry-specific questions
              </li>
              <li>
                Priority Support: Enhanced customer service response times
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              2. SUBSCRIPTION PLANS AND PRICING
            </h2>

            <h3 className="text-xl font-semibold mb-3">2.1 Available Plans</h3>

            <h4 className="font-semibold mb-2">Monthly Subscription</h4>
            <ul className="list-disc pl-6 mb-4">
              <li>Billing Cycle: Every 30 days</li>
              <li>Payment: Charged monthly in advance</li>
              <li>
                Cancellation: Can be cancelled anytime with effect at end of
                current billing period
              </li>
              <li>Price: As displayed in the app and on globalcoders.app</li>
            </ul>

            <h4 className="font-semibold mb-2">Quarterly Subscription</h4>
            <ul className="list-disc pl-6 mb-4">
              <li>Billing Cycle: Every 90 days (3 months)</li>
              <li>Payment: Charged quarterly in advance</li>
              <li>Savings: Discounted rate compared to monthly billing</li>
              <li>
                Cancellation: Can be cancelled anytime with effect at end of
                current billing period
              </li>
              <li>Price: As displayed in the app and on globalcoders.app</li>
            </ul>

            <h4 className="font-semibold mb-2">Annual Subscription</h4>
            <ul className="list-disc pl-6 mb-4">
              <li>Billing Cycle: Every 365 days (12 months)</li>
              <li>Payment: Charged annually in advance</li>
              <li>Savings: Maximum discount compared to monthly billing</li>
              <li>
                Cancellation: Can be cancelled anytime with effect at end of
                current billing period
              </li>
              <li>Price: As displayed in the app and on globalcoders.app</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">2.2 Pricing Display</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>
                All prices displayed in user's local currency where supported
              </li>
              <li>Prices include applicable taxes where required by law</li>
              <li>
                Current pricing available at globalcoders.app and within the app
              </li>
              <li>
                Educational or volume discounts may be available upon request
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              2.3 Geographic Pricing
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Pricing may vary by geographic region</li>
              <li>Local taxes and fees applied as required by law</li>
              <li>Currency conversion handled by payment processors</li>
              <li>Price parity maintained where commercially reasonable</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              3. BILLING AND PAYMENT PROCESSING
            </h2>

            <h3 className="text-xl font-semibold mb-3">3.1 Payment Methods</h3>
            <p className="mb-2">Accepted Payment Methods:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Credit cards (Visa, Mastercard, American Express)</li>
              <li>Debit cards where supported</li>
              <li>Digital wallets (Apple Pay, Google Pay, PayPal)</li>
              <li>Bank transfers (select regions)</li>
              <li>Local payment methods as available</li>
            </ul>

            <p className="mb-2 font-semibold">Payment Security:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                All payments processed through PCI DSS compliant processors
              </li>
              <li>
                No storage of complete payment card information on our servers
              </li>
              <li>Tokenization used for recurring billing security</li>
              <li>Fraud detection and prevention measures implemented</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">3.2 Billing Process</h3>

            <p className="mb-2 font-semibold">Initial Subscription:</p>
            <ol className="list-decimal pl-6 mb-4">
              <li>User selects subscription plan</li>
              <li>Payment method verification and authorization</li>
              <li>Immediate charge for first billing period</li>
              <li>Subscription activation and feature access granted</li>
              <li>Confirmation email sent with billing details</li>
            </ol>

            <p className="mb-2 font-semibold">Recurring Billing:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Automatic charges on renewal dates</li>
              <li>Email notifications sent 7 days before renewal</li>
              <li>Failed payment retry attempts (up to 3 times over 7 days)</li>
              <li>Account suspension if payment cannot be processed</li>
              <li>Grace period of 7 days to update payment information</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              3.3 Billing Dates and Cycles
            </h3>

            <p className="mb-2 font-semibold">Billing Date Calculation:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Monthly: Same date each month (or last day if date doesn't
                exist)
              </li>
              <li>Quarterly: Same date every 3 months</li>
              <li>Annual: Same date each year</li>
            </ul>

            <p className="mb-2 font-semibold">Prorated Billing:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Plan upgrades: Immediate charge for prorated difference</li>
              <li>Plan downgrades: Credit applied to next billing cycle</li>
              <li>
                Mid-cycle cancellations: No prorated refunds (service continues
                until period end)
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              3.4 Invoicing and Receipts
            </h3>
            <ul className="list-disc pl-6 mb-4">
              <li>Electronic receipts sent via email for all transactions</li>
              <li>Detailed invoices available in account settings</li>
              <li>VAT/tax information included where applicable</li>
              <li>Business receipts available upon request</li>
              <li>Historical billing records accessible for 7 years</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              4. AUTOMATIC RENEWAL AND CANCELLATION
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              4.1 Automatic Renewal
            </h3>
            <p className="mb-2">Renewal Process:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Subscriptions automatically renew unless cancelled</li>
              <li>Renewal occurs at the end of each billing period</li>
              <li>
                Same subscription plan and pricing (subject to price changes)
              </li>
              <li>Payment charged to the same payment method</li>
              <li>Confirmation email sent after successful renewal</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              4.2 Cancellation Process
            </h3>

            <p className="mb-2 font-semibold">How to Cancel:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>In-App: Profile &gt; Subscription</li>
              <li>
                Email: Send cancellation request to support@globalcoders.app
              </li>
              <li>Website: Account dashboard at globalcoders.app</li>
              <li>
                App Store: Through Apple App Store or Google Play Store
                subscription management
              </li>
            </ul>

            <p className="mb-2 font-semibold">Cancellation Timing:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Must cancel at least 24 hours before next renewal</li>
              <li>
                Cancellation takes effect at end of current billing period
              </li>
              <li>
                Immediate cancellation not available (service continues until
                period end)
              </li>
              <li>No partial refunds for unused time in current period</li>
            </ul>

            <p className="mb-2 font-semibold">Cancellation Confirmation:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Immediate email confirmation of cancellation request</li>
              <li>Service continues until end of paid period</li>
              <li>Account converts to free tier after subscription expires</li>
              <li>Re-subscription available at any time</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              4.3 Failed Payment Handling
            </h3>

            <p className="mb-2 font-semibold">Payment Failure Process:</p>
            <ol className="list-decimal pl-6 mb-4">
              <li>Initial Failure: Immediate retry attempt</li>
              <li>Day 3: Second retry attempt with email notification</li>
              <li>Day 7: Final retry attempt with urgent notification</li>
              <li>
                Day 10: Account suspended, service restricted to free tier
              </li>
              <li>Day 30: Account marked for deletion (with 30-day notice)</li>
            </ol>

            <p className="mb-2 font-semibold">Account Recovery:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Update payment method to restore service immediately</li>
              <li>All data and progress preserved during suspension period</li>
              <li>
                No additional fees for payment failure (unless imposed by
                payment processor)
              </li>
              <li>Automatic service restoration upon successful payment</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. REFUND POLICY</h2>

            <h3 className="text-xl font-semibold mb-3">
              5.1 General Refund Policy
            </h3>

            <p className="mb-2 font-semibold">Standard Policy:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Subscriptions are generally non-refundable</li>
              <li>No refunds for unused portions of subscription periods</li>
              <li>No refunds for free tier usage (no payment made)</li>
              <li>
                Refunds considered on case-by-case basis for technical issues
              </li>
            </ul>

            <p className="mb-2 font-semibold">Exceptions:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Technical issues preventing service use for extended periods
              </li>
              <li>Billing errors or unauthorized charges</li>
              <li>
                Legal requirements under applicable consumer protection laws
              </li>
              <li>Discretionary refunds at company's sole discretion</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              5.2 EU Consumer Rights
            </h3>

            <p className="mb-2 font-semibold">Right of Withdrawal:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                EU consumers have 14-day withdrawal right for distance contracts
              </li>
              <li>
                Right begins from contract conclusion (subscription start)
              </li>
              <li>Must be exercised before service performance begins</li>
              <li>
                Waived if user explicitly requests immediate service access
              </li>
            </ul>

            <p className="mb-2 font-semibold">
              Refund Process for EU Consumers:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Submit withdrawal request to support@globalcoders.app</li>
              <li>Include order details and reason for withdrawal</li>
              <li>
                Refund processed within 14 days of valid withdrawal request
              </li>
              <li>Refund made to original payment method</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              5.3 Refund Request Process
            </h3>

            <p className="mb-2 font-semibold">How to Request Refund:</p>
            <ol className="list-decimal pl-6 mb-4">
              <li>
                Email support@globalcoders.app with subject "Refund Request"
              </li>
              <li>Include account email, subscription details, and reason</li>
              <li>Provide supporting documentation if applicable</li>
              <li>Allow 5-10 business days for review and response</li>
            </ol>

            <p className="mb-2 font-semibold">Refund Evaluation Criteria:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Technical issues documented and unresolved</li>
              <li>Service unavailability exceeding reasonable limits</li>
              <li>Billing errors or processing mistakes</li>
              <li>Compliance with applicable consumer protection laws</li>
              <li>Account history and usage patterns</li>
            </ul>

            <p className="mb-2 font-semibold">Refund Processing:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Approved refunds processed within 10 business days</li>
              <li>Refunds made to original payment method</li>
              <li>Account access adjusted based on refund amount</li>
              <li>Confirmation email sent when refund is processed</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. PRICE CHANGES</h2>

            <h3 className="text-xl font-semibold mb-3">
              6.1 Price Change Policy
            </h3>

            <p className="mb-2 font-semibold">Advance Notice:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Minimum 30 days written notice for price increases</li>
              <li>Notice sent via email to registered account address</li>
              <li>In-app notifications for active users</li>
              <li>Website posting of new pricing structure</li>
            </ul>

            <p className="mb-2 font-semibold">Grandfathering:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Current subscribers maintain existing pricing for remainder of
                current billing period
              </li>
              <li>New pricing applies to next renewal after notice period</li>
              <li>Option to cancel before price increase takes effect</li>
              <li>No retroactive price changes to completed billing periods</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              6.2 Price Change Implementation
            </h3>

            <p className="mb-2 font-semibold">Notification Content:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Clear explanation of pricing changes</li>
              <li>Effective date of new pricing</li>
              <li>Impact on user's specific subscription</li>
              <li>Options for cancellation or plan changes</li>
              <li>Contact information for questions</li>
            </ul>

            <p className="mb-2 font-semibold">User Options:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Continue subscription at new pricing</li>
              <li>Cancel subscription before price change</li>
              <li>Downgrade to different plan if available</li>
              <li>Contact support for assistance or questions</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              7. SUBSCRIPTION MODIFICATIONS
            </h2>

            <h3 className="text-xl font-semibold mb-3">7.1 Plan Upgrades</h3>

            <p className="mb-2 font-semibold">Upgrade Process:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Immediate access to upgraded features</li>
              <li>Prorated charge for difference in plan cost</li>
              <li>New billing cycle begins from upgrade date</li>
              <li>Confirmation email with updated billing information</li>
            </ul>

            <p className="mb-2 font-semibold">Upgrade Calculations:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Credit for unused time on current plan</li>
              <li>Charge for full period of new plan</li>
              <li>Net difference charged immediately</li>
              <li>Future billing at new plan rate and schedule</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">7.2 Plan Downgrades</h3>

            <p className="mb-2 font-semibold">Downgrade Process:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Downgrade takes effect at end of current billing period</li>
              <li>No immediate charge or refund</li>
              <li>Features restricted according to new plan</li>
              <li>Confirmation email with effective date</li>
            </ul>

            <p className="mb-2 font-semibold">Feature Access:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Premium features remain available until downgrade effective date
              </li>
              <li>Data and progress preserved during transition</li>
              <li>Access restrictions applied according to new plan limits</li>
              <li>Upgrade option available at any time</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              7.3 Subscription Transfers
            </h3>

            <p className="mb-2 font-semibold">Account Transfers:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Subscriptions are non-transferable between accounts</li>
              <li>
                Business account transfers considered on case-by-case basis
              </li>
              <li>
                Corporate subscriptions may have different transfer policies
              </li>
              <li>Contact support@globalcoders.app for transfer requests</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. TAXES AND FEES</h2>

            <h3 className="text-xl font-semibold mb-3">
              8.1 Tax Responsibility
            </h3>

            <p className="mb-2 font-semibold">Consumer Taxes:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Applicable sales tax, VAT, or GST added to subscription price
              </li>
              <li>Tax rates determined by user's billing address</li>
              <li>Tax calculations handled by payment processors</li>
              <li>Tax invoices provided where required by law</li>
            </ul>

            <p className="mb-2 font-semibold">Business Taxes:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Business users responsible for applicable business taxes</li>
              <li>
                VAT-registered businesses may be eligible for reverse charge
              </li>
              <li>Tax identification numbers can be added to account</li>
              <li>Detailed tax invoices available for business accounts</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">8.2 Additional Fees</h3>

            <p className="mb-2 font-semibold">Payment Processing Fees:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>No additional fees charged by GlobalCoders</li>
              <li>
                Payment processor fees may apply (varies by method and region)
              </li>
              <li>
                Currency conversion fees may apply for international payments
              </li>
              <li>
                Bank fees for certain payment methods (user responsibility)
              </li>
            </ul>

            <p className="mb-2 font-semibold">Third-Party Fees:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                App store fees (Apple App Store, Google Play) included in
                pricing
              </li>
              <li>
                No additional charges for app store subscription management
              </li>
              <li>Refund processing fees may apply in certain circumstances</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              9. BUSINESS AND ENTERPRISE SUBSCRIPTIONS
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              9.1 Business Account Pricing
            </h3>

            <p className="mb-2 font-semibold">Pricing:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Volume discounts available for 10+ users</li>
              <li>Annual billing required for enterprise accounts</li>
              <li>Custom pricing for large organizations</li>
              <li>
                Educational discounts available for qualifying institutions
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              9.2 Enterprise Billing
            </h3>

            <p className="mb-2 font-semibold">Payment Terms:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Net 30 payment terms available for qualified businesses</li>
              <li>Purchase order processing available</li>
              <li>Custom billing cycles (quarterly, annually)</li>
              <li>Detailed usage reporting and analytics</li>
            </ul>

            <p className="mb-2 font-semibold">Contract Terms:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Minimum commitment periods may apply</li>
              <li>Custom terms and conditions for enterprise clients</li>
              <li>Service level agreements available</li>
              <li>Dedicated account management for large accounts</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              10. ACCOUNT SUSPENSION AND TERMINATION
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              10.1 Suspension Reasons
            </h3>

            <p className="mb-2 font-semibold">Payment-Related Suspension:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Failed payment after retry attempts</li>
              <li>Disputed or charged-back payments</li>
              <li>Fraudulent payment activity</li>
              <li>Expired or invalid payment methods</li>
            </ul>

            <p className="mb-2 font-semibold">Policy Violation Suspension:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Violation of Terms and Conditions</li>
              <li>Abuse of service or resources</li>
              <li>Inappropriate content or behavior</li>
              <li>Security violations or unauthorized access</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              10.2 Suspension Process
            </h3>

            <p className="mb-2 font-semibold">Notification:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Email notification of suspension reason</li>
              <li>Instructions for account restoration</li>
              <li>Timeline for resolution</li>
              <li>Contact information for appeals</li>
            </ul>

            <p className="mb-2 font-semibold">Service Restrictions:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Access limited to free tier features</li>
              <li>Data preserved during suspension period</li>
              <li>No new charges during suspension</li>
              <li>Restoration available upon issue resolution</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              10.3 Account Termination
            </h3>

            <p className="mb-2 font-semibold">Termination Reasons:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Extended payment failure (30+ days)</li>
              <li>Repeated policy violations</li>
              <li>Fraudulent activity</li>
              <li>User-requested account deletion</li>
            </ul>

            <p className="mb-2 font-semibold">Data Handling:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>30-day grace period before data deletion</li>
              <li>User notification before permanent deletion</li>
              <li>Data export available upon request</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              11. DISPUTE RESOLUTION
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              11.1 Billing Disputes
            </h3>

            <p className="mb-2 font-semibold">Dispute Process:</p>
            <ol className="list-decimal pl-6 mb-4">
              <li>Contact support@globalcoders.app within 60 days of charge</li>
              <li>Provide detailed explanation of dispute</li>
              <li>Include supporting documentation</li>
              <li>Allow 10 business days for investigation and response</li>
            </ol>

            <p className="mb-2 font-semibold">Resolution Options:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Billing correction and adjustment</li>
              <li>Partial or full refund where appropriate</li>
              <li>Account credit for future billing periods</li>
              <li>Service restoration or modification</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              11.2 Chargeback Protection
            </h3>

            <p className="mb-2 font-semibold">Chargeback Prevention:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Clear billing descriptors on statements</li>
              <li>Detailed transaction receipts and confirmations</li>
              <li>Proactive customer communication</li>
              <li>Easy cancellation and refund processes</li>
            </ul>

            <p className="mb-2 font-semibold">Chargeback Response:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Immediate investigation of chargeback claims</li>
              <li>Documentation provided to payment processors</li>
              <li>Account suspension during chargeback investigation</li>
              <li>Resolution communication to all parties</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              12. LEGAL AND REGULATORY COMPLIANCE
            </h2>

            <h3 className="text-xl font-semibold mb-3">
              12.1 Consumer Protection Laws
            </h3>

            <p className="mb-2 font-semibold">EU Consumer Rights:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>14-day withdrawal right for distance contracts</li>
              <li>Clear pricing and terms disclosure</li>
              <li>Automatic renewal consent requirements</li>
              <li>Unfair contract terms protection</li>
            </ul>

            <p className="mb-2 font-semibold">US Consumer Protection:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Automatic renewal law compliance (various states)</li>
              <li>Clear and conspicuous disclosure requirements</li>
              <li>Easy cancellation mechanisms</li>
              <li>Truth in advertising standards</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              12.2 Payment Card Industry Compliance
            </h3>

            <p className="mb-2 font-semibold">PCI DSS Compliance:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Secure payment processing through certified processors</li>
              <li>No storage of sensitive payment card data</li>
              <li>Regular security assessments and audits</li>
              <li>Incident response procedures for payment security</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              12.3 International Regulations
            </h3>

            <p className="mb-2 font-semibold">Data Protection:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>GDPR compliance for EU users</li>
              <li>CCPA compliance for California residents</li>
              <li>Local privacy law compliance as applicable</li>
              <li>Cross-border data transfer protections</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              13. CONTACT INFORMATION
            </h2>

            <h3 className="text-xl font-semibold mb-3">13.1 Billing Support</h3>
            <ul className="list-none mb-4">
              <li>
                <strong>Email:</strong> support@globalcoders.app
              </li>
              <li>
                <strong>Subject Line:</strong> Billing Inquiry
              </li>
              <li>
                <strong>Response Time:</strong> Within 2 business days
              </li>
              <li>
                <strong>Hours:</strong> Monday-Friday, 9:00 AM - 6:00 PM CET
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              13.2 Subscription Management
            </h3>
            <ul className="list-none mb-4">
              <li>
                <strong>Email:</strong> support@globalcoders.app
              </li>
              <li>
                <strong>Subject Line:</strong> Subscription Management
              </li>
              <li>
                <strong>Response Time:</strong> Within 1 business day
              </li>
              <li>
                <strong>Self-Service:</strong> Available through app and website
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">13.3 Refund Requests</h3>
            <ul className="list-none mb-4">
              <li>
                <strong>Email:</strong> support@globalcoders.app
              </li>
              <li>
                <strong>Subject Line:</strong> Refund Request
              </li>
              <li>
                <strong>Response Time:</strong> Within 5 business days
              </li>
              <li>
                <strong>Required Information:</strong> Account details, reason
                for refund, supporting documentation
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-3">
              13.4 Enterprise Sales
            </h3>
            <ul className="list-none mb-4">
              <li>
                <strong>Email:</strong> support@globalcoders.app
              </li>
              <li>
                <strong>Subject Line:</strong> Enterprise Inquiry
              </li>
              <li>
                <strong>Response Time:</strong> Within 1 business day
              </li>
              <li>
                <strong>Services:</strong> Volume pricing, custom terms,
                dedicated support
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              14. EFFECTIVE DATE AND UPDATES
            </h2>

            <h3 className="text-xl font-semibold mb-3">14.1 Effective Date</h3>
            <p className="mb-4">
              These Subscription and Billing Terms are effective as of 21st
              October 2025 and apply to all subscriptions purchased on or after
              this date.
            </p>

            <h3 className="text-xl font-semibold mb-3">
              14.2 Updates and Changes
            </h3>

            <p className="mb-2 font-semibold">Notification Process:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>Material changes communicated with 30 days advance notice</li>
              <li>Notice sent via email to registered account addresses</li>
              <li>In-app notifications for active users</li>
              <li>Updated terms posted on website with effective date</li>
            </ul>

            <p className="mb-2 font-semibold">Acceptance:</p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                Continued use of service constitutes acceptance of updated terms
              </li>
              <li>Option to cancel subscription if changes are unacceptable</li>
              <li>
                No retroactive application to existing subscription periods
              </li>
            </ul>

            <p className="mb-4">
              <strong>Document Version:</strong> 1.0
              <br />
              <strong>Last Updated:</strong> 21st October 2025
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative bg-gradient-to-br from-primary via-accent to-primary mt-20">
        {/* Decorative grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Main footer content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* About section */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                Global Coders
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                AI-powered English conversation practice for tech professionals.
                Build confidence, improve communication, and ace your next
                conversation.
              </p>
            </div>

            {/* Quick Links */}

            {/* Resources */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/faq"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-of-service"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/subscription-billing"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    Subscription and Billing Terms
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                Get in Touch
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                Have questions or feedback? We'd love to hear from you. Contact
                us on support@globalcoders.app - we do respond to all emails!
              </p>
            </div>
          </div>

          {/* Funding section */}
          <div className="border-t border-white/10 pt-8 pb-8">
            <div className="flex justify-center items-center mb-8">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-3 shadow-2xl">
                <img
                  src={footerFunding}
                  alt="Financiado por la Unión Europea - NextGenerationEU. Con el apoyo de la Generalitat de Catalunya"
                  className="max-w-full h-auto"
                  style={{
                    maxHeight: "140px",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/60 text-sm">
                © {new Date().getFullYear()} Global Coders. All rights
                reserved.
              </p>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <span>Made with</span>
                <span className="text-white/80">❤️</span>
                <span>for developers worldwide</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SubscriptionBillingTerms;
